<template>
  <SideSheet :is-open="isOpen" position="right" @close="$emit('close')">
    <div class="account-state-side-sheet">
      <NavBar sticky>
        <template #left>
          <ButtonIconContextual
            icon="EF0030"
            label=""
            @click.native="$emit('close')"
          />
        </template>
      </NavBar>
      <div class="account-state-side-sheet__content">
        <div v-if="loading" class="account-state-side-sheet__loader">
          <LoaderIndicator />
        </div>
        <div v-else-if="error" class="account-state-side-sheet__error">
          <ActionableHelperView :on-retry="fetchAccountState" />
        </div>
        <template v-else-if="accountState">
          <div class="account-state-side-sheet__header">
            <Heading class="account-state-side-sheet__title" :level="2">
              {{ accountState.title }}
            </Heading>
            <BaseText
              class="account-state-side-sheet__description"
              size="p4"
              :color="NebraskaColors.textSecondary"
            >
              {{ accountState.description }}
            </BaseText>
          </div>
          <div class="account-state-side-sheet__steps">
            <AccountStateRow
              v-for="(step, index) in steps"
              :key="index"
              :icon="step.icon"
              :is-completed="step.isCompleted"
              :is-current="step.isCurrent"
              :is-disabled="step.isDisabled"
              :title="step.title"
              :description="step.description"
              :status="step.status"
              :chip-type="step.chipType"
              :show-divider="index < steps.length - 1"
            />
          </div>
        </template>
      </div>
      <ButtonContainer
        v-if="accountState && !loading && !error"
        class="account-state-side-sheet__button-container"
      >
        <template #button-primary>
          <ButtonPrimary
            :label="accountState.button.label"
            @click="handleButtonClick"
          />
        </template>
      </ButtonContainer>
    </div>
  </SideSheet>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import {
  NavBar,
  Heading,
  BaseText,
  ButtonIconContextual,
  LoaderIndicator,
  ActionableHelperView,
  ButtonContainer,
  ButtonPrimary
} from '@warrenbrasil/nebraska-web';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { SideSheet } from '@/modules/common/components/SideSheet';
import {
  AccountStateResponse,
  AccountStateStep,
  AccountStateStatus,
  AccountStateButtonAction,
  AccountStateChipType
} from '@/modules/common/services/http/account/account-state-types';
import { getAccountStateDetail } from '@/modules/common/services/http/account/account';
import { getDomainAndPlatform } from '@/modules/common/helpers/onboarding/getDomainAndPlatform';
import AccountStateRow from './components/AccountStateRow.vue';

interface Step {
  icon?: number;
  isCompleted: boolean;
  isCurrent: boolean;
  isDisabled: boolean;
  title: string;
  description: string;
  status?: string;
  chipType?: AccountStateChipType;
}

@Component({
  name: 'AccountStateSideSheet',
  components: {
    NavBar,
    Heading,
    BaseText,
    ButtonIconContextual,
    LoaderIndicator,
    ActionableHelperView,
    ButtonContainer,
    ButtonPrimary,
    SideSheet,
    AccountStateRow
  }
})
export default class AccountStateSideSheet extends Vue {
  public readonly NebraskaColors = NebraskaColors;

  @Prop({ type: Boolean, default: false })
  public isOpen!: boolean;

  private accountState: AccountStateResponse | null = null;
  private loading = false;
  private error = false;

  @Watch('isOpen')
  private onIsOpenChange(newValue: boolean) {
    if (newValue && !this.accountState) {
      this.fetchAccountState();
    }
  }

  private get steps(): Step[] {
    if (!this.accountState) return [];

    return this.accountState.steps.map(step => {
      const isCompleted = step.status === AccountStateStatus.Completed;
      const isCurrent = step.status === AccountStateStatus.Current;
      const isDisabled = step.status === AccountStateStatus.Disabled;

      return {
        icon: step.icon,
        isCompleted,
        isCurrent,
        isDisabled,
        title: step.title,
        description: step.description,
        status: step.chipLabel,
        chipType: step.chipType
      };
    });
  }

  private async fetchAccountState() {
    this.loading = true;
    this.error = false;

    try {
      this.accountState = await getAccountStateDetail();
    } catch (err) {
      this.error = true;
    } finally {
      this.loading = false;
    }
  }

  private handleButtonClick() {
    if (!this.accountState) return;

    const { domain, platform } = getDomainAndPlatform();
    const action = this.accountState.button.action;

    const routeMapping: Record<
      AccountStateButtonAction,
      { mobile: string; desktop: string; withHost?: boolean }
    > = {
      [AccountStateButtonAction.RegisterPersonal]: {
        mobile: 'personal-info/nationality',
        desktop: 'personal-info'
      },
      [AccountStateButtonAction.RegisterFinancialLife]: {
        mobile: 'financial-life/profession',
        desktop: 'financial-info'
      },
      [AccountStateButtonAction.RegisterDocuments]: {
        mobile: 'register-documents/document',
        desktop: 'documents-info'
      }
    };

    const route = routeMapping[action];
    if (!route) return;

    const routePath = route[platform];
    const href = route.withHost
      ? routePath
      : `/onboarding/${domain}/${platform}/${routePath}`;

    window.location.href = href;
  }
}
</script>

<style lang="less" scoped>
.account-state-side-sheet {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: @background-secondary;

  &__content {
    flex: 1;
    overflow-y: auto;
    padding: @size-spacing-x500;
  }

  &__loader,
  &__error {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
  }

  &__header {
    margin-bottom: @size-spacing-x500;
  }

  &__title {
    margin-bottom: @size-spacing-x400;
    color: @text-primary;
  }

  &__description {
    margin-bottom: @size-spacing-x400;
  }

  &__steps {
    margin-top: @size-spacing-x500;
  }

  &__button-container {
    padding: @size-spacing-x500;
    border-top: 1px solid @divider-primary;
  }
}

:deep(.panel-content.right) {
  @media screen and (min-width: 1024px) {
    width: 683px;
  }
}
</style>
